/**
 * The battlefield played by hand (round 3; data/guildField.ts): the player warps into สมรภูมิธงสามผืน when the war
 * starts and fights with their own character, while the rest of both sides are the match's simulated fighters
 * (`GuildWarMatch`) set down as entities and moved by it.
 *
 * - **In and out:** `enter` sets the player down at their camp (where they stood is kept) and `leave` (or the war's
 *   end) brings them back there. The player is the match's fighter 0, driven (`place`) from where they stand.
 * - **Allies** are `npc` entities that mirror their fighter (the match fights for them). **Enemies** are monsters
 *   (`GUILD_FOE_ID`): away from the player they mirror their fighter too; within `engage` m they leave the match's
 *   AI (driven) and fight the player with the monster rules, and their fall is the player's defeat of them.
 * - **The player falling** is a defeat by the nearest enemy: no return to town, a 10 s wait, then up at the camp.
 * - The HUD and the banners read `guildWar` `field` events, a few a second while the player is inside.
 */
import { GUILD_WAR_FIGHT } from '../data/pvpTemplate';
import { GUILD_FIELD, GUILD_WAR } from '../data/guild';
import { coreOf } from '../region/core';
import { fighterLook, GUILD_FIELD_PLAY, GUILD_FOE_ID, guildFieldOrigin } from '../data/guildField';
import type { Entity } from '../world';
import type { GuildWarMatch } from './guildWar';
import type { SystemHost } from './types';

/** What the HUD and the banners show (the `field` event). */
export interface GuildFieldState {
  /** The field's corner on the map. */
  readonly origin: { readonly x: number; readonly y: number };
  readonly sides: readonly { readonly name: string; readonly color: string }[];
  readonly points: readonly number[];
  readonly banners: readonly {
    readonly id: string;
    readonly holder: number | null;
    readonly capSide: number | null;
    /** How far the capture has gone (0–1). */
    readonly cap: number;
  }[];
  /** Seconds the player still waits to stand up (0: on their feet). */
  readonly respawn: number;
  /** Seconds of war left. */
  readonly left: number;
}

export class GuildField {
  /** Inside the battlefield. */
  inside = false;
  private origin = { x: 0, y: 0 };
  /** The map the field was entered on (a zone change or a door drops the field without a warp back). */
  private map: unknown = null;
  private back = { x: 0, y: 0 };
  /** The entity of each fighter (by the match's index), the player's own left out. */
  private readonly ents = new Map<number, Entity>();
  private stateT = 0;

  constructor(
    private readonly h: SystemHost,
    private readonly colors: () => readonly string[],
    private readonly left: () => number,
    /** Whether tonight's war is against players (items v2: the forge counts at most +10 then). */
    private readonly pvp: () => boolean = () => false,
  ) {}

  /** Whether this zone's map has a battlefield to warp into. */
  available(): boolean {
    return guildFieldOrigin(this.h.world.map.zone) !== null && !this.h.world.interiors.inside;
  }

  enter(m: GuildWarMatch): void {
    const at = guildFieldOrigin(this.h.world.map.zone);
    if (this.inside || !at || this.h.world.interiors.inside) return;
    const p = this.h.world.player;
    this.h.stopMeditating();
    this.h.dismount();
    this.inside = true;
    // The field's origin is in the town's own metres; in a region the town sits at the core's origin.
    const o = coreOf(this.h.world.map);
    this.origin = { x: at.x + o.x, y: at.y + o.y };
    this.back = { x: p.x, y: p.y };
    this.map = this.h.world.map;
    const me = m.fighters[0]!;
    const camp = GUILD_FIELD.camps[0]!;
    this.setPlayer(camp.x, camp.y);
    m.place(0, camp.x, camp.y);
    me.alive = !p.dead;
    for (let i = 1; i < m.fighters.length; i++) this.ents.set(i, this.spawn(m, i));
    this.stateT = 0;
    if (this.pvp()) {
      GUILD_WAR_FIGHT.add(this.h.world);
      this.h.world.refreshPlayerStats();
    }
    this.h.emit({ type: 'guildWar', what: 'enter' });
    this.h.notice(`เข้าสู่${GUILD_FIELD.name}`);
  }

  /** Out of the battlefield, back where the player stood; their fighter is simulated again. */
  leave(m: GuildWarMatch | null): void {
    if (!this.inside) return;
    this.inside = false;
    const w = this.h.world;
    if (GUILD_WAR_FIGHT.delete(w)) w.refreshPlayerStats();
    for (const e of this.ents.values()) {
      w.entities.delete(e.id);
      if (w.player.targetId === e.id) w.player.targetId = null;
    }
    this.ents.clear();
    if (m) m.fighters[0]!.driven = false;
    this.h.emit({ type: 'guildWar', what: 'leave' });
    // Left by a zone's warp or a door: the player is already elsewhere.
    if (w.map !== this.map) return;
    const p = w.player;
    if (p.dead) Object.assign(p, { dead: false, deadT: 0, hp: p.maxHp });
    Object.assign(p, { x: this.back.x, y: this.back.y, z: 0, vz: 0, path: null });
    this.h.input.engage = false;
  }

  /** Before the match's tick: the player's and the engaged enemies' places into the match, the falls. */
  before(m: GuildWarMatch): void {
    if (!this.inside) return;
    if (this.h.world.map !== this.map) return this.leave(m);
    const p = this.h.world.player;
    const me = m.fighters[0]!;
    if (p.dead && me.alive) {
      m.defeat(this.nearestFoe(m), 0);
      this.h.log('sys', `ถูกล้มในศึกกิลด์ ฟื้นที่ค่ายใน ${GUILD_WAR.respawn} วินาที`);
    }
    // The World's own respawn (back to town) waits; the match stands the player up.
    if (p.dead) p.deadT = GUILD_WAR.respawn + 5;
    if (me.alive && !p.dead) m.place(0, p.x - this.origin.x, p.y - this.origin.y);
    for (const [i, e] of this.ents) {
      const f = m.fighters[i]!;
      if (f.side === 0) continue;
      if (f.alive && e.dead) {
        m.defeat(0, i);
        f.driven = false;
        continue;
      }
      if (!f.alive) continue;
      const d = Math.hypot(e.x - p.x, e.y - p.y);
      if (!p.dead && d <= GUILD_FIELD_PLAY.engage) f.driven = true;
      else if (p.dead || d > GUILD_FIELD_PLAY.release) f.driven = false;
      if (f.driven) {
        f.x = e.x - this.origin.x;
        f.y = e.y - this.origin.y;
      }
    }
  }

  /** After the match's tick: the entities follow their fighters; the player stands up at the camp. */
  after(m: GuildWarMatch, dt: number): void {
    if (!this.inside) return;
    const p = this.h.world.player;
    const me = m.fighters[0]!;
    if (p.dead && me.alive) {
      const camp = GUILD_FIELD.camps[0]!;
      Object.assign(p, {
        dead: false,
        deadT: 0,
        hp: p.maxHp,
        qi: p.maxQi,
        stamina: p.maxStamina,
        stunT: 0,
        statuses: [],
      });
      this.setPlayer(camp.x, camp.y);
      m.place(0, camp.x, camp.y);
      this.h.emit({ type: 'respawn', id: p.id });
    }
    for (const [i, e] of this.ents) {
      const f = m.fighters[i]!;
      if (!f.alive) {
        if (!e.dead) {
          Object.assign(e, { hp: 0, dead: true, deadT: 0, cast: null, moving: false });
          this.h.emit({ type: 'death', id: e.id });
        }
        e.deadT = 0;
        continue;
      }
      if (e.dead) {
        Object.assign(e, { dead: false, deadT: 0, hp: e.maxHp, cast: null, stunT: 0, statuses: [] });
        if (e.brain) e.brain.state = 'idle';
      }
      if (f.driven) continue;
      const x = this.origin.x + f.x;
      const y = this.origin.y + f.y;
      const dx = x - e.x;
      e.moving = Math.hypot(dx, y - e.y) > 0.01;
      if (Math.abs(dx) > 0.01) e.facing = dx > 0 ? 1 : -1;
      e.x = x;
      e.y = y;
      if (e.brain) {
        e.brain.spawnX = x;
        e.brain.spawnY = y;
      }
    }
    this.stateT -= dt;
    if (this.stateT <= 0) {
      this.stateT = GUILD_FIELD_PLAY.stateEvery;
      this.h.emit({ type: 'guildWar', what: 'field', state: this.state(m) });
    }
  }

  state(m: GuildWarMatch): GuildFieldState {
    const colors = this.colors();
    const me = m.fighters[0]!;
    return {
      origin: { ...this.origin },
      sides: m.sides.map((s, i) => ({ name: s.name, color: colors[i] ?? '#888888' })),
      points: m.points.map((x) => Math.floor(x)),
      banners: m.banners.map((b) => ({
        id: b.id,
        holder: b.holder,
        capSide: b.capSide,
        cap: Math.min(1, b.capT / GUILD_WAR.captureTime),
      })),
      respawn: me.alive ? 0 : Math.max(0, me.respawnAt - m.t),
      left: Math.max(0, Math.round(this.left())),
    };
  }

  private setPlayer(fx: number, fy: number): void {
    const p = this.h.world.player;
    Object.assign(p, { x: this.origin.x + fx, y: this.origin.y + fy, z: 0, vz: 0, path: null, cast: null });
    this.h.input.engage = false;
  }

  private nearestFoe(m: GuildWarMatch): number {
    const me = m.fighters[0]!;
    let best = -1;
    let bd = Infinity;
    m.fighters.forEach((f, i) => {
      if (f.side === 0 || !f.alive) return;
      const d = (f.x - me.x) ** 2 + (f.y - me.y) ** 2;
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    return best < 0 ? m.fighters.findIndex((f) => f.side !== 0) : best;
  }

  private spawn(m: GuildWarMatch, i: number): Entity {
    const f = m.fighters[i]!;
    const look = fighterLook(f.name);
    const x = this.origin.x + f.x;
    const y = this.origin.y + f.y;
    const side = m.sides[f.side]!;
    if (f.side === 0) {
      return this.h.add({
        kind: 'npc',
        x,
        y,
        name: f.name,
        title: side.name,
        sprite: look.sprite,
        hp: 100,
        maxHp: 100,
      });
    }
    const e = this.h.spawnMonster(GUILD_FOE_ID, x, y);
    const hp = Math.round(f.power * GUILD_FIELD_PLAY.hpPerPower);
    Object.assign(e, { name: f.name, title: side.name, sprite: look.sprite, hp, maxHp: hp, wander: null });
    return e;
  }
}
